import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Renders a JSON-LD <script> tag inside <head> via react-helmet-async.
 * Schema.org structured data is picked up by Google, Bing, etc. for rich results.
 * It updates automatically when the `data` prop changes (new product, edit, etc.).
 */
export default function StructuredData({ data }) {
  if (!data) return null;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

// Canonical site URL (used to build absolute URLs in structured data).
// Override at runtime by setting REACT_APP_SITE_URL in frontend/.env.
export const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://joeycyan.com');

/** Resolve a relative image path to an absolute URL. */
export function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  if (pathOrUrl.startsWith('/api/')) return `${backend}${pathOrUrl}`;
  return `${SITE_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/** Build a Product schema from a product document. */
export function buildProductSchema(product, { currency = 'EUR', slug } = {}) {
  if (!product) return null;
  const images = []
    .concat(product.images || [])
    .concat(product.gallery_urls || [])
    .filter(Boolean)
    .map(absoluteUrl);

  const offerAvailability =
    (product.stock ?? 1) > 0
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description: product.description || product.subtitle || product.meta_description || '',
    image: images.length ? images : undefined,
    sku: product.sku || product.id || slug,
    brand: { '@type': 'Brand', name: 'Joey Cyan' },
    category: product.category || (product.categories && product.categories[0]) || undefined,
    url: `${SITE_URL}/produit/${slug || product.slug}`,
    offers: {
      '@type': 'Offer',
      price: Number(product.price || 0).toFixed(2),
      priceCurrency: currency,
      availability: offerAvailability,
      url: `${SITE_URL}/produit/${slug || product.slug}`,
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
        .toISOString()
        .split('T')[0],
      seller: { '@type': 'Organization', name: 'Joey Cyan' },
    },
  };

  // Aggregate rating — only include if we have a real rating AND real review count
  if (product.rating && product.review_count && product.review_count > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(product.rating).toFixed(1),
      reviewCount: Number(product.review_count),
      bestRating: '5',
      worstRating: '1',
    };
  }

  return schema;
}

/** Build a BlogPosting schema from a blog post document. */
export function buildBlogPostingSchema(post, { slug } = {}) {
  if (!post) return null;
  const url = `${SITE_URL}/blog/${slug || post.slug}`;
  const image = absoluteUrl(post.og_image || post.image || post.featured_image);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.meta_description || post.excerpt || '',
    image: image ? [image] : undefined,
    author: { '@type': 'Organization', name: post.author || 'Joey Cyan' },
    publisher: {
      '@type': 'Organization',
      name: 'Joey Cyan',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo192.png`,
      },
    },
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at || post.published_at || post.created_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  };
}

/** Build a FAQPage schema from an array of {question, answer} items. */
export function buildFAQSchema(items) {
  if (!items || !items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: String(it.answer || '').replace(/<[^>]*>/g, ''), // strip HTML
      },
    })),
  };
}

/** Global Organization schema (shown on every page via Layout). */
export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Joey Cyan',
    url: SITE_URL,
    logo: `${SITE_URL}/logo192.png`,
    description:
      'Joey Cyan — lunettes connectées AR : optique, solaire, caméra. Bluetooth, IA embarquée, HeyCyan app.',
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@joeycyan.com',
      contactType: 'customer support',
      areaServed: 'FR',
      availableLanguage: ['French', 'English'],
    },
  };
}

/** WebSite schema with SearchAction (enables Google sitelinks searchbox). */
export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Joey Cyan',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/collection?search={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}
